import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { css, Label, Grid, Modal, Toast, Spinner, Badge, SectionHeader, PageHeader, IconBtn, ROL_COLORS, ROL_LABELS } from '../components/UI';
import { BulkImportModal, exportarLayout } from '../components/BulkImport';
import { ALL_MENU_ITEMS } from '../config/menu';
import * as XLSX from 'xlsx';
import ReglasAprobacion from '../components/ReglasAprobacion';
import { CuentasProveedorEditor, cuentasDelProveedor, cuentaVacia } from '../components/CuentasProveedor';

// Ítems de menú personalizables por usuario. Se excluyen 'admin'/'catalogos':
// esas pantallas dependen de rutas del backend que solo aceptan rol admin/finanzas,
// así que otorgarlas por menú a otro rol solo mostraría un panel roto (403 al usar).
const MENU_PERSONALIZABLE = ALL_MENU_ITEMS.filter(i => i.id !== 'admin' && i.id !== 'catalogos');

// ── Genérico CRUD ──────────────────────────────────────────────────────────
// onToggleActivo (opcional): cuando el catálogo se desactiva en lugar de borrarse,
// sustituye la papelera por un botón Desactivar/Reactivar y atenúa los inactivos.
// Los catálogos que no lo pasan siguen igual, con la papelera de siempre.
function CrudTable({ titulo, cols, items, onAdd, onEdit, onDelete, onToggleActivo, loading, renderCell, footer, extraActions }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{titulo}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {extraActions}
          <button onClick={onAdd} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : items.length === 0 ? (
          <div style={{ padding: '52px 32px', textAlign: 'center' }}>
            <div style={{ width:60, height:60, borderRadius:18, background:'var(--hair-soft)', border:'1.5px solid var(--hair)', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:26, marginBottom:14 }}>📭</div>
            <div style={{ fontSize:14, fontWeight:600, color:'var(--ink-soft)', marginBottom:4 }}>Sin registros</div>
            <div style={{ fontSize:12, color:'var(--glass)' }}>Agrega el primer elemento con el botón +</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'linear-gradient(to bottom, var(--hair-soft), var(--hair-soft))', borderBottom: '1px solid var(--hair)' }}>
                {[...cols, 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)', transition: 'background 0.12s', opacity: onToggleActivo && item.activo === false ? 0.55 : 1 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {cols.map(c => <td key={c} style={{ padding: '9px 14px' }}>{renderCell(c, item)}</td>)}
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <IconBtn onClick={() => onEdit(item)} title="Editar" icon="✏️" />
                      {onToggleActivo ? (
                        <button onClick={() => onToggleActivo(item)}
                          title={item.activo ? 'Deja de aparecer en las solicitudes; se conserva el historial' : 'Vuelve a estar disponible'}
                          style={{ padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
                            border: `1px solid ${item.activo ? 'rgba(239,68,68,0.28)' : 'rgba(22,163,74,0.28)'}`,
                            background: item.activo ? 'rgba(239,68,68,0.10)' : 'rgba(22,163,74,0.10)',
                            color: item.activo ? '#dc2626' : '#15803d' }}>
                          {item.activo ? 'Desactivar' : 'Reactivar'}
                        </button>
                      ) : (
                        <IconBtn onClick={() => onDelete(item._id)} title="Eliminar" icon="🗑" danger />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {footer}
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

function Paginacion({ page, total, limit, onChange }) {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  const buildPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  const btn = (active, disabled) => ({
    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: active ? 700 : 400,
    cursor: disabled ? 'not-allowed' : 'pointer', border: '1px solid',
    borderColor: active ? 'var(--plasma)' : 'var(--hair)',
    background: active ? 'var(--plasma)' : disabled ? 'var(--hair-soft)' : 'var(--hair-soft)',
    color: active ? 'white' : disabled ? 'var(--glass)' : 'var(--ink-soft)',
    fontFamily: 'Inter, sans-serif', transition: 'all 0.12s',
    boxShadow: active ? '0 2px 10px rgba(255,84,28,0.35)' : 'none',
  });

  const from = Math.min((page - 1) * limit + 1, total);
  const to = Math.min(page * limit, total);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--hair)', background: 'var(--hair-soft)' }}>
      <span style={{ fontSize: 12, color: 'var(--glass)' }}>
        Mostrando <strong>{from}</strong>–<strong>{to}</strong> de <strong>{total}</strong> registros
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button onClick={() => onChange(page - 1)} disabled={page === 1} style={btn(false, page === 1)}>‹ Ant</button>
        {buildPages().map((p, i) =>
          p === '...'
            ? <span key={`e${i}`} style={{ padding: '4px 6px', color: 'var(--glass)', fontSize: 13 }}>…</span>
            : <button key={p} onClick={() => onChange(p)} style={btn(p === page, false)}>{p}</button>
        )}
        <button onClick={() => onChange(page + 1)} disabled={page === totalPages} style={btn(false, page === totalPages)}>Sig ›</button>
      </div>
    </div>
  );
}

// ── Usuarios ───────────────────────────────────────────────────────────────
function Usuarios() {
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [centros, setCentros] = useState([]);
  // Series contables derivadas de cada catálogo de motivos, por tipo de solicitud
  const [seriesPorTipoCat, setSeriesPorTipoCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', email: '', password: '', rol: 'solicitante', roles: [], empresas: [], sucursales: [], centrosCostos: [], direccion: '', seriesContables: [], seriesContablesPorTipo: {}, menuPersonalizado: false, menuIds: [], catalogosPermitidos: [] });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Usuarios',
    columnas: [
      { key: 'nombre',   label: 'Nombre',                               required: true,  ejemplo: 'Juan Pérez' },
      { key: 'email',    label: 'Email',                                required: true,  ejemplo: 'juan@empresa.com' },
      { key: 'password', label: 'Password',                             required: true,  ejemplo: 'Pass2024!' },
      { key: 'rol',      label: 'Rol (admin/finanzas/solicitante)',      required: true,  ejemplo: 'solicitante' },
      { key: 'empresas', label: 'Empresas (separadas por |)',            required: false, ejemplo: 'BH|BLOOM' },
    ],
    onImport: async (rows) => api.post('/auth/usuarios/import', { items: rows }),
  };

  // Búsqueda por nombre/email (debounce) + filtro por rol + paginación en servidor
  const [q, setQ] = useState('');
  const [rolFiltro, setRolFiltro] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [exportando, setExportando] = useState(false);
  const load = () => setRefresh(r => r + 1);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/auth/usuarios?q=${encodeURIComponent(q)}&rol=${encodeURIComponent(rolFiltro)}&page=${page}&limit=${PAGE_SIZE}`)
        .then(r => { setItems(r.items || []); setTotal(r.total || 0); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, rolFiltro, page, refresh]);

  // Descarga el layout COMPLETO de usuarios en Excel (todas las columnas;
  // las primeras 5 coinciden con la plantilla de importación).
  const exportarLayoutUsuarios = async () => {
    setExportando(true);
    try {
      const r = await api.get('/auth/usuarios?limit=2000&page=1');
      const rows = (r.items || []).map(u => ({
        nombre: u.nombre,
        email: u.email,
        password: '', // vacío a propósito: el archivo sirve como layout de importación
        rol: u.rol,
        empresas: (u.empresas || []).join('|'),
        roles_adicionales: (u.roles || []).join('|'),
        sucursales: (u.sucursales || []).join('|'),
        centros_costos: (u.centrosCostos || []).join('|'),
        direccion: u.direccion || '',
        series_contables: (u.seriesContables || []).join('|'),
        activo: u.activo ? 'SI' : 'NO',
        creado: u.createdAt ? new Date(u.createdAt).toLocaleDateString('es-MX') : '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 28 }, { wch: 32 }, { wch: 10 }, { wch: 14 }, { wch: 24 }, { wch: 20 }, { wch: 24 }, { wch: 28 }, { wch: 20 }, { wch: 16 }, { wch: 7 }, { wch: 11 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
      XLSX.writeFile(wb, `layout_usuarios_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast_(`Layout descargado: ${rows.length} usuarios`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setExportando(false); }
  };

  // Catálogos para el formulario (una sola vez)
  useEffect(() => {
    Promise.all([
      api.get('/empresas'),
      api.get('/centros/direcciones').catch(() => []),
      api.get('/sucursales').catch(() => []),
      api.get('/centros/all?limit=2000').catch(() => ({ items: [] })),
      api.get('/series-contables').catch(() => ({})),
    ]).then(([emps, dirs, sucs, centrosRes, series]) => {
      setEmpresas(emps); setDirecciones(dirs || []);
      setSucursales(sucs || []); setCentros(centrosRes.items || []);
      setSeriesPorTipoCat(series || {});
    });
  }, []);

  const open = (item = null) => {
    setEditing(item);
    setForm(item
      ? { nombre: item.nombre, email: item.email, password: '', rol: item.rol, roles: item.roles || [], empresas: item.empresas || [], sucursales: item.sucursales || [], centrosCostos: item.centrosCostos || [], direccion: item.direccion || '', seriesContables: item.seriesContables || [], seriesContablesPorTipo: item.seriesContablesPorTipo || {}, menuPersonalizado: !!item.menuPersonalizado, menuIds: item.menuIds || [], catalogosPermitidos: item.catalogosPermitidos || [] }
      : { nombre: '', email: '', password: '', rol: 'solicitante', roles: [], empresas: [], sucursales: [], centrosCostos: [], direccion: '', seriesContables: [], seriesContablesPorTipo: {}, menuPersonalizado: false, menuIds: [], catalogosPermitidos: [] });
    setModal(true);
  };

  const toggleEnLista = (campo, valor) => setForm(f => ({
    ...f,
    [campo]: f[campo].includes(valor)
      ? f[campo].filter(x => x !== valor)
      : [...f[campo], valor],
  }));
  const toggleEmp = (nombre) => toggleEnLista('empresas', nombre);

  // Alterna una serie contable de un tipo separado (débito, contribuciones, etc.)
  const toggleSerieTipo = (tipo, serie) => setForm(f => {
    const cur = (f.seriesContablesPorTipo || {})[tipo] || [];
    const next = cur.includes(serie) ? cur.filter(x => x !== serie) : [...cur, serie];
    return { ...f, seriesContablesPorTipo: { ...(f.seriesContablesPorTipo || {}), [tipo]: next } };
  });

  const save = async () => {
    try {
      // El rol principal no se duplica en los adicionales
      const payload = { ...form, roles: (form.roles || []).filter(r => r !== form.rol) };
      if (editing) await api.put(`/auth/usuarios/${editing._id}`, payload);
      else await api.post('/auth/usuarios', payload);
      toast_(`Usuario ${editing ? 'actualizado' : 'creado'}`);
      setModal(false); load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Desactivar impide iniciar sesión, pero no borra nada: las solicitudes siguen
  // apuntando al usuario y su nombre se sigue viendo en historial y reportes.
  const toggleActivo = async (u) => {
    const msg = u.activo
      ? `¿Desactivar a ${u.nombre}?\n\nNo podrá iniciar sesión. Sus solicitudes y su historial se conservan.`
      : `¿Reactivar a ${u.nombre}?\n\nVolverá a poder iniciar sesión.`;
    if (!window.confirm(msg)) return;
    try {
      if (u.activo) await api.del(`/auth/usuarios/${u._id}`);
      else await api.put(`/auth/usuarios/${u._id}`, { activo: true });
      toast_(`Usuario ${u.activo ? 'desactivado' : 'reactivado'}`);
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={load} />
      <CrudTable titulo={`Usuarios${total ? ` (${total})` : ''}`} cols={['Nombre', 'Email', 'Rol', 'Compañía', 'Estado']} items={items} loading={loading}
        onAdd={() => open()} onEdit={open} onToggleActivo={toggleActivo}
        footer={<Paginacion page={page} total={total} limit={PAGE_SIZE} onChange={setPage} />}
        extraActions={<>
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="🔍 Buscar por nombre o email..."
            style={{ ...css.input(false), width: 240, padding: '8px 12px', fontSize: 12.5 }} />
          <select value={rolFiltro} onChange={e => { setRolFiltro(e.target.value); setPage(1); }}
            title="Filtrar por rol (principal o adicional)"
            style={{ ...css.select(false), width: 170, padding: '8px 32px 8px 12px', fontSize: 12.5 }}>
            <option value="">Todos los roles</option>
            {Object.entries(ROL_LABELS).map(([r, label]) => <option key={r} value={r}>{label}</option>)}
          </select>
          <button onClick={exportarLayoutUsuarios} disabled={exportando} title="Descarga el layout completo de usuarios en Excel"
            style={{ ...css.btn('#15803d'), fontSize: 12, opacity: exportando ? 0.6 : 1 }}>
            {exportando ? 'Generando…' : '⬇️ Layout'}
          </button>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
        </>}
        renderCell={(col, item) => {
          if (col === 'Nombre') return <span style={{ fontWeight: 500 }}>{item.nombre}</span>;
          if (col === 'Email') return <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>{item.email}</span>;
          if (col === 'Rol') return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Badge color={ROL_COLORS[item.rol]}>{ROL_LABELS[item.rol]}</Badge>
              {item.roles?.length > 0 && (
                <span title={`Roles adicionales: ${item.roles.map(r => ROL_LABELS[r] || r).join(', ')}`}
                  style={{ fontSize: 10, color: 'var(--glass)', fontWeight: 700 }}>+{item.roles.length}</span>
              )}
            </span>
          );
          if (col === 'Compañía') return item.empresas?.length
            ? <span style={{ fontSize: 12, color: 'var(--ink)' }}>{item.empresas.length === 1 ? item.empresas[0] : `${item.empresas.length} empresas`}</span>
            : <span style={{ fontSize: 11, color: 'var(--glass)' }}>—</span>;
          if (col === 'Estado') return <Badge color={item.activo ? '#15803d' : '#dc2626'}>{item.activo ? 'Activo' : 'Inactivo'}</Badge>;
        }}
      />
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Usuario' : 'Nuevo Usuario'}>
        <Grid cols={1} gap={12}>
          <div><Label required>Nombre</Label><input value={form.nombre} onChange={e => set('nombre', e.target.value)} style={css.input(false)} /></div>
          <div><Label required>Email</Label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} style={css.input(false)} /></div>
          <div><Label required={!editing}>Contraseña {editing && '(dejar vacío para no cambiar)'}</Label><input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={editing ? 'Sin cambios' : 'Temporal: la cambiará al entrar'} style={css.input(false)} /></div>
          <div>
            <Label required>Rol</Label>
            <select value={form.rol} onChange={e => set('rol', e.target.value)} style={css.select(false)}>
              <option value="solicitante">Solicitante</option>
              <option value="finanzas">Finanzas</option>
              <option value="admin">Administrador</option>
              <option value="director">Director (solo lectura, por dirección)</option>
              <option value="contador">Contador (aprueba, por compañías)</option>
              <option value="programacion">Programación (paga lo aprobado)</option>
              <option value="jefe_compras">Jefe de Compras (aprueba requisiciones)</option>
              <option value="comprador">Comprador (cotiza y compra)</option>
            </select>
          </div>
          <div>
            <Label>Roles adicionales (podrá alternar entre ellos desde el menú)</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
              {Object.entries(ROL_LABELS).filter(([r]) => r !== form.rol).map(([r, label]) => {
                const sel = form.roles.includes(r);
                return (
                  <button key={r} onClick={() => toggleEnLista('roles', r)} style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                    background: sel ? (ROL_COLORS[r] || '#ff541c') : 'var(--hair-soft)',
                    color: sel ? '#0e0f11' : 'var(--ink-soft)',
                    border: `1.5px solid ${sel ? (ROL_COLORS[r] || '#ff541c') : 'var(--hair)'}`,
                  }}>{label}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
              {form.roles.length === 0
                ? 'Sin roles adicionales — solo tendrá el rol principal'
                : `Podrá cambiar entre ${[form.rol, ...form.roles].filter((r, i, a) => a.indexOf(r) === i).map(r => ROL_LABELS[r]).join(' / ')}`}
            </div>
          </div>
          <div>
            <Label>Series contables · Solicitudes de pago (clasifican sus gastos automáticamente)</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
              {[['6000', 'Operación / Distribución'], ['6100', 'Venta'], ['6200', 'Administración'], ['6300', 'Depreciaciones'], ['6400', 'Amortizaciones']].map(([s, nom]) => {
                const sel = form.seriesContables.includes(s);
                return (
                  <button key={s} onClick={() => toggleEnLista('seriesContables', s)} style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                    background: sel ? 'var(--plasma)' : 'var(--hair-soft)',
                    color: sel ? '#fff' : 'var(--ink-soft)',
                    border: `1.5px solid ${sel ? 'var(--plasma)' : 'var(--hair)'}`,
                  }}>{s} · {nom}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
              {form.seriesContables.length === 0
                ? 'Sin series — la cuenta contable la elige el contador al aprobar.'
                : 'Sus solicitudes tomarán la cuenta del motivo dentro de estas series; si aplica en varias, la define el contador.'}
            </div>
          </div>
          {/* Series contables de los tipos separados (débito, contribuciones, activos fijos,
              mercancías nac.). Los prefijos se derivan del catálogo de cada tipo. */}
          {[['debito', 'Débito'], ['contribuciones', 'Contribuciones'], ['activos_fijos', 'Activos Fijos'], ['mercancias_nacionales', 'Mercancías Nacionales'], ['nomina', 'Nómina']].map(([tipo, label]) => {
            const opciones = seriesPorTipoCat[tipo] || [];
            const seleccion = (form.seriesContablesPorTipo || {})[tipo] || [];
            return (
              <div key={tipo}>
                <Label>Series contables · {label}</Label>
                {opciones.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4, fontStyle: 'italic' }}>
                    El catálogo de motivos de {label.toLowerCase()} aún no tiene cuentas contables capturadas.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
                      {opciones.map(s => {
                        const sel = seleccion.includes(s);
                        return (
                          <button key={s} onClick={() => toggleSerieTipo(tipo, s)} style={{
                            padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                            fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                            background: sel ? 'var(--plasma)' : 'var(--hair-soft)',
                            color: sel ? '#fff' : 'var(--ink-soft)',
                            border: `1.5px solid ${sel ? 'var(--plasma)' : 'var(--hair)'}`,
                          }}>{s}</button>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
                      {seleccion.length === 0
                        ? `Sin series — la cuenta de sus solicitudes de ${label.toLowerCase()} la elige el contador al aprobar.`
                        : `Sus solicitudes de ${label.toLowerCase()} tomarán la cuenta del motivo dentro de estas series.`}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {form.rol === 'director' && (
            <div>
              <Label required>Dirección asignada</Label>
              <select value={form.direccion} onChange={e => set('direccion', e.target.value)} style={css.select(false)}>
                <option value="">— Selecciona una dirección —</option>
                {direcciones.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
                Solo verá el Dashboard e historiales de esta dirección (solo lectura).
              </div>
              {!form.direccion && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 3 }}>Sin dirección no verá ninguna solicitud.</div>}
            </div>
          )}
          {['solicitante', 'contador'].includes(form.rol) && (
            <div>
              <Label required={form.rol === 'contador'}>Empresas asignadas</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 160, overflowY: 'auto' }}>
                {empresas.map(e => {
                  const sel = form.empresas.includes(e.nombre);
                  return (
                    <button key={e._id} onClick={() => toggleEmp(e.nombre)} style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                      background: sel ? e.color || 'var(--plasma)' : 'var(--hair-soft)',
                      color: sel ? 'white' : 'var(--ink-soft)',
                      border: `1.5px solid ${sel ? e.color || 'var(--plasma)' : 'var(--hair)'}`,
                    }}>{e.nombre}</button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
                {form.empresas.length === 0
                  ? (form.rol === 'contador' ? '' : 'Sin selección — verá todas las empresas')
                  : `${form.empresas.length} empresa(s) seleccionada(s)`}
              </div>
              {form.rol === 'contador' && form.empresas.length === 0 &&
                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 3 }}>Sin empresas asignadas, el contador no verá ninguna solicitud.</div>}
              {form.rol === 'contador' &&
                <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 3 }}>Aprueba/rechaza solicitudes pendientes de estas empresas (primer filtro del flujo de pago).</div>}
            </div>
          )}
          {form.rol === 'solicitante' && (() => {
            // Cascada: las sucursales se muestran según las empresas elegidas.
            // Una empresa sin lista de sucursales no restringe (muestra todas).
            const empresasSel = empresas.filter(e => form.empresas.includes(e.nombre));
            const algunaSinLista = empresasSel.some(e => !(e.sucursales?.length));
            const sucursalesVisibles = (form.empresas.length === 0 || algunaSinLista)
              ? sucursales
              : sucursales.filter(s => empresasSel.some(e => e.sucursales.includes(s.nombre)));
            return (
              <div>
                <Label>Sucursales permitidas</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 160, overflowY: 'auto' }}>
                  {sucursalesVisibles.length === 0
                    ? <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>No hay sucursales asociadas a las empresas elegidas.</span>
                    : sucursalesVisibles.map(s => {
                      const sel = form.sucursales.includes(s.nombre);
                      return (
                        <button key={s._id} onClick={() => toggleEnLista('sucursales', s.nombre)} style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                          fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                          background: sel ? '#0369a1' : 'var(--hair-soft)', color: sel ? 'white' : 'var(--ink-soft)',
                          border: `1.5px solid ${sel ? '#0369a1' : 'var(--hair)'}`,
                        }}>{s.nombre}</button>
                      );
                    })}
                </div>
                <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
                  {form.sucursales.length === 0
                    ? 'Sin selección — podrá pedir en todas las sucursales'
                    : `${form.sucursales.length} sucursal(es) seleccionada(s)`}
                  {form.empresas.length > 0 && ' · se muestran solo las de las empresas elegidas'}
                </div>
              </div>
            );
          })()}
          {form.rol === 'solicitante' && (() => {
            const centrosVisibles = form.sucursales.length
              ? centros.filter(c => (c.sucursales || []).some(su => form.sucursales.includes(su)))
              : centros;
            // Agrupar por sección para ordenar la selección
            const secciones = [...new Set(centrosVisibles.map(c => (c.seccion || '').trim()))]
              .sort((a, b) => (a || 'zzz').localeCompare(b || 'zzz'));
            const chip = (c) => {
              const sel = form.centrosCostos.includes(c.nombre);
              return (
                <button key={c._id} onClick={() => toggleEnLista('centrosCostos', c.nombre)} title={c.seccion || ''} style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                  background: sel ? '#7c3aed' : 'var(--hair-soft)', color: sel ? 'white' : 'var(--ink-soft)',
                  border: `1.5px solid ${sel ? '#7c3aed' : 'var(--hair)'}`,
                }}>{c.nombre}</button>
              );
            };
            return (
              <div>
                <Label>Centros de costos permitidos</Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 240, overflowY: 'auto' }}>
                  {centrosVisibles.length === 0
                    ? <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>{form.sucursales.length ? 'No hay centros asociados a las sucursales elegidas.' : 'No hay centros de costos.'}</span>
                    : secciones.map(sec => (
                      <div key={sec || '__sin__'}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>
                          {sec || 'Sin sección'}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                          {centrosVisibles.filter(c => (c.seccion || '').trim() === sec).map(chip)}
                        </div>
                      </div>
                    ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
                  {form.centrosCostos.length === 0
                    ? 'Sin selección — podrá usar todos los centros de sus sucursales'
                    : `${form.centrosCostos.length} centro(s) seleccionado(s)`}
                  {form.sucursales.length > 0 && ' · se muestran solo los de las sucursales elegidas'}
                </div>
              </div>
            );
          })()}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.menuPersonalizado}
                onChange={e => set('menuPersonalizado', e.target.checked)} />
              <Label>Menú personalizado para este usuario</Label>
            </label>
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 2, marginBottom: form.menuPersonalizado ? 8 : 0 }}>
              {form.menuPersonalizado
                ? 'Verá exactamente lo que marques abajo, sin importar su rol.'
                : 'Desactivado — el menú se calcula según su rol, como siempre.'}
            </div>
            {form.menuPersonalizado && (() => {
              const grupos = [...new Set(MENU_PERSONALIZABLE.map(i => i.grupo || 'General'))];
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 260, overflowY: 'auto' }}>
                  {grupos.map(g => (
                    <div key={g}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--plasma)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>{g}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                        {MENU_PERSONALIZABLE.filter(i => (i.grupo || 'General') === g).map(i => {
                          const sel = form.menuIds.includes(i.id);
                          return (
                            <button key={i.id} onClick={() => toggleEnLista('menuIds', i.id)} style={{
                              padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                              fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                              background: sel ? 'var(--plasma)' : 'var(--hair-soft)', color: sel ? 'white' : 'var(--ink-soft)',
                              border: `1.5px solid ${sel ? 'var(--plasma)' : 'var(--hair)'}`,
                            }}>{i.icon} {i.label}</button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {form.menuPersonalizado && form.menuIds.length === 0 &&
              <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>Sin ítems marcados, este usuario no verá ninguna opción de menú.</div>}
          </div>
          {!['admin', 'finanzas'].includes(form.rol) && (
            <div>
              <Label>Catálogos que puede editar</Label>
              <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 2, marginBottom: 8 }}>
                Además de lo que le da su rol, podrá crear/editar (no borrar) estos catálogos, sin importar su rol.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
                {TABS_ADMIN.filter(t => t.roles.length > 1).map(t => {
                  const sel = form.catalogosPermitidos.includes(t.id);
                  return (
                    <button key={t.id} onClick={() => toggleEnLista('catalogosPermitidos', t.id)} style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                      background: sel ? 'var(--plasma)' : 'var(--hair-soft)', color: sel ? 'white' : 'var(--ink-soft)',
                      border: `1.5px solid ${sel ? 'var(--plasma)' : 'var(--hair)'}`,
                    }}>{t.label}</button>
                  );
                })}
              </div>
              {form.catalogosPermitidos.length === 0
                ? <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>Sin catálogos asignados — solo los que ya le da su rol (ninguno, salvo materiales para compradores).</div>
                : <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>Podrá editar {form.catalogosPermitidos.length} catálogo(s) además de los de su rol.</div>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Empresas ───────────────────────────────────────────────────────────────
function Empresas() {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', abrev: '', color: 'var(--plasma)', sucursales: [] });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Empresas',
    columnas: [
      { key: 'nombre', label: 'Nombre',       required: true,  ejemplo: 'BH COMERCIALIZADORA S.A.' },
      { key: 'abrev',  label: 'Abreviatura',  required: true,  ejemplo: 'BH' },
      { key: 'color',  label: 'Color (hex)',  required: false, ejemplo: '#2ecc71' },
    ],
    onImport: async (rows) => api.post('/empresas/import', { items: rows }),
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/empresas'), api.get('/sucursales')])
      .then(([emps, sucs]) => { setItems(emps); setSucursales(sucs); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const open = (item = null) => {
    setEditing(item);
    setForm(item
      ? { nombre: item.nombre, abrev: item.abrev, color: item.color || 'var(--plasma)', sucursales: item.sucursales || [] }
      : { nombre: '', abrev: '', color: 'var(--plasma)', sucursales: [] });
    setModal(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/empresas/${editing._id}`, form);
      else await api.post('/empresas', form);
      toast_('Empresa guardada'); setModal(false); load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Desactivar empresa?')) return;
    try { await api.del(`/empresas/${id}`); toast_('Empresa desactivada'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleSuc = (nombre) => setForm(f => ({
    ...f,
    sucursales: f.sucursales.includes(nombre)
      ? f.sucursales.filter(s => s !== nombre)
      : [...f.sucursales, nombre],
  }));

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={load} />
      <CrudTable titulo="Empresas" cols={['Nombre', 'Abrev', 'Color', 'Sucursales']} items={items} loading={loading}
        onAdd={() => open()} onEdit={open} onDelete={del}
        extraActions={<button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>}
        renderCell={(col, item) => {
          if (col === 'Nombre') return <span style={{ fontWeight: 500 }}>{item.nombre}</span>;
          if (col === 'Abrev') return <Badge color={item.color || 'var(--plasma)'}>{item.abrev}</Badge>;
          if (col === 'Color') return <div style={{ width: 20, height: 20, borderRadius: 4, background: item.color }} />;
          if (col === 'Sucursales') return (
            <span style={{ color: 'var(--glass)', fontSize: 12 }}>
              {item.sucursales?.length ? `${item.sucursales.length} asignada(s)` : <span style={{ color: '#f59e0b' }}>Sin asignar</span>}
            </span>
          );
        }}
      />
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Empresa' : 'Nueva Empresa'} width={560}>
        <Grid cols={1} gap={12}>
          <div><Label required>Nombre</Label><input value={form.nombre} onChange={e => set('nombre', e.target.value)} style={css.input(false)} /></div>
          <Grid cols={2} gap={12}>
            <div><Label required>Abreviatura</Label><input value={form.abrev} onChange={e => set('abrev', e.target.value)} placeholder="Ej. BH" style={css.input(false)} /></div>
            <div><Label>Color</Label><div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><input type="color" value={form.color} onChange={e => set('color', e.target.value)} style={{ width: 44, height: 36, borderRadius: 6, border: '1px solid var(--hair)', cursor: 'pointer' }} /><span style={{ fontSize: 13, color: 'var(--glass)' }}>{form.color}</span></div></div>
          </Grid>
          <div>
            <Label>Sucursales que le corresponden</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 180, overflowY: 'auto' }}>
              {sucursales.map(suc => {
                const selected = form.sucursales.includes(suc.nombre);
                return (
                  <button key={suc._id} onClick={() => toggleSuc(suc.nombre)} style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                    background: selected ? form.color || 'var(--plasma)' : 'var(--hair-soft)',
                    color: selected ? 'white' : 'var(--ink-soft)',
                    border: `1.5px solid ${selected ? form.color || 'var(--plasma)' : 'var(--hair)'}`,
                  }}>{suc.nombre}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 5 }}>
              {form.sucursales.length === 0
                ? 'Sin selección — el formulario mostrará todas las sucursales'
                : `${form.sucursales.length} sucursal(es) seleccionada(s)`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Proveedores ────────────────────────────────────────────────────────────
function Proveedores() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [motivosCat, setMotivosCat] = useState([]);
  const [motivoQ, setMotivoQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', beneficiario: '', cuentas: [], moneda: 'MXN', clave: '', categoria: '', rfc: '', email: '', motivosPago: [] });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Proveedores',
    columnas: [
      { key: 'nombre',      label: 'Nombre / Razón Social', required: true,  ejemplo: 'PROVEEDOR ABC S.A.' },
      { key: 'beneficiario',label: 'Beneficiario',          required: false, ejemplo: 'JUAN GARCIA' },
      { key: 'banco',       label: 'Banco',                 required: false, ejemplo: 'BBVA' },
      { key: 'clabe',       label: 'CLABE',                 required: false, ejemplo: '012345678901234567' },
      { key: 'cuenta',      label: 'No. Cuenta',            required: false, ejemplo: '1234567890' },
      { key: 'moneda',      label: 'Moneda',                required: false, ejemplo: 'MXN' },
      { key: 'clave',       label: 'Clave',                 required: false, ejemplo: '10001' },
      { key: 'categoria',   label: 'Categoría',             required: false, ejemplo: 'COMPRAS INDIRECTAS' },
      { key: 'rfc',         label: 'RFC',                   required: false, ejemplo: 'XAXX010101000' },
      { key: 'email',       label: 'Email',                 required: false, ejemplo: 'prov@correo.com' },
    ],
    onImport: async (rows) => api.post('/proveedores/import', { items: rows }),
  };

  const load = (query = q, p = page) => {
    setLoading(true);
    // todos=1: el catálogo del admin muestra también los desactivados, para reactivarlos
    api.get(`/proveedores?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}&todos=1`).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => {
    load('');
    api.get('/motivos').then(ms => setMotivosCat(ms.map(m => m.nombre))).catch(() => {});
  }, []); // eslint-disable-line

  const EMPTY_PROV = { nombre: '', beneficiario: '', cuentas: [cuentaVacia(true)], moneda: 'MXN', clave: '', categoria: '', rfc: '', email: '', motivosPago: [] };

  const open = (item = null) => {
    setMotivoQ('');
    setEditing(item);
    setForm(item ? {
      nombre: item.nombre, beneficiario: item.beneficiario || '',
      cuentas: cuentasDelProveedor(item, true), moneda: item.moneda || 'MXN',
      clave: item.clave || '', categoria: item.categoria || '', rfc: item.rfc || '', email: item.email || '',
      motivosPago: item.motivosPago || [],
    } : { ...EMPTY_PROV });
    setModal(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/proveedores/${editing._id}`, form);
      else await api.post('/proveedores', form);
      toast_('Proveedor guardado'); setModal(false); load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Desactivar lo saca del buscador de las solicitudes, pero no borra nada: las
  // solicitudes guardan el nombre del proveedor, así que el historial no se toca.
  const toggleActivo = async (p) => {
    const msg = p.activo
      ? `¿Desactivar a ${p.nombre}?\n\nDejará de aparecer al capturar solicitudes. Las solicitudes que ya lo usan no se modifican.`
      : `¿Reactivar a ${p.nombre}?\n\nVolverá a aparecer al capturar solicitudes.`;
    if (!window.confirm(msg)) return;
    try {
      if (p.activo) await api.del(`/proveedores/${p._id}`);
      else await api.put(`/proveedores/${p._id}`, { activo: true });
      toast_(`Proveedor ${p.activo ? 'desactivado' : 'reactivado'}`);
      load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Descarga el catálogo con el layout de la plantilla de importación, así el archivo
  // sirve de respaldo y se puede reimportar. Se piden todos de una vez: la tabla está
  // paginada y el buscador no debe recortar la descarga.
  // Sin todos=1 a propósito: solo van los ACTIVOS. El import reactiva lo que carga, así
  // que incluir los desactivados los revivería en silencio al reimportar el respaldo.
  const exportar = async () => {
    setExportando(true);
    try {
      const { items } = await api.get('/proveedores?limit=100000');
      if (!items?.length) { toast_('No hay proveedores para exportar', 'error'); return; }
      // Un renglón por cuenta activa: al reimportar, cada renglón vuelve a sumar su cuenta
      const filas = items.flatMap(p => {
        const cs = cuentasDelProveedor(p);
        return cs.length ? cs.map(c => ({ ...p, banco: c.banco, clabe: c.clabe, cuenta: c.cuenta })) : [p];
      });
      exportarLayout(importConfig.titulo, importConfig.columnas, filas);
      toast_(`${items.length} proveedor${items.length !== 1 ? 'es' : ''} exportado${items.length !== 1 ? 's' : ''}`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setExportando(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleMotivo = (nombre) => setForm(f => ({
    ...f,
    motivosPago: f.motivosPago.includes(nombre)
      ? f.motivosPago.filter(m => m !== nombre)
      : [...f.motivosPago, nombre],
  }));
  const motivosFiltrados = motivoQ
    ? motivosCat.filter(m => m.toLowerCase().includes(motivoQ.toLowerCase()))
    : motivosCat;

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }} placeholder="Buscar por nombre, RFC, categoría..." style={{ ...css.input(false), width: 300 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={exportar} disabled={exportando} title="Descarga todos los proveedores con el layout de la plantilla de importación"
            style={{ ...css.btn('#15803d'), fontSize: 12, opacity: exportando ? 0.6 : 1 }}>
            {exportando ? 'Generando...' : '⬇️ Descargar'}
          </button>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Clave', 'Nombre', 'Categoría', 'RFC', 'Banco', 'CLABE', 'Moneda', 'Estado', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(p => (
                <tr key={p._id} style={{ borderBottom: '1px solid var(--hair-soft)', opacity: p.activo === false ? 0.55 : 1 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--glass)' }}>{p.clave || '—'}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.categoria || '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>{p.rfc || '—'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)' }}>{p.banco || '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {p.clabe || '—'}
                    {cuentasDelProveedor(p).length > 1 && (
                      <span title="Cuentas activas del proveedor" style={{ marginLeft: 6, fontFamily: 'Inter, sans-serif', fontSize: 10.5, fontWeight: 700, color: 'var(--plasma)' }}>
                        +{cuentasDelProveedor(p).length - 1}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '9px 14px' }}><Badge color="#0369a1">{p.moneda}</Badge></td>
                  <td style={{ padding: '9px 14px' }}><Badge color={p.activo ? '#15803d' : '#dc2626'}>{p.activo ? 'Activo' : 'Inactivo'}</Badge></td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(p)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => toggleActivo(p)}
                        title={p.activo ? 'Deja de aparecer al capturar solicitudes; se conserva el historial' : 'Vuelve a estar disponible'}
                        style={{ padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
                          border: `1px solid ${p.activo ? 'rgba(239,68,68,0.28)' : 'rgba(22,163,74,0.28)'}`,
                          background: p.activo ? 'rgba(239,68,68,0.10)' : 'rgba(22,163,74,0.10)',
                          color: p.activo ? '#dc2626' : '#15803d' }}>
                        {p.activo ? 'Desactivar' : 'Reactivar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Proveedor' : 'Nuevo Proveedor'} width={560}>
        <Grid cols={2} gap={12}>
          <div style={{ gridColumn: 'span 2' }}><Label required>Nombre / Razón Social</Label><input value={form.nombre} onChange={e => set('nombre', e.target.value)} style={css.input(false)} /></div>
          <div style={{ gridColumn: 'span 2' }}><Label>Beneficiario (si difiere del nombre)</Label><input value={form.beneficiario} onChange={e => set('beneficiario', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Clave</Label><input value={form.clave} onChange={e => set('clave', e.target.value)} placeholder="10001" style={css.input(false)} /></div>
          <div><Label>Categoría</Label><input value={form.categoria} onChange={e => set('categoria', e.target.value)} placeholder="COMPRAS INDIRECTAS..." style={css.input(false)} /></div>
          <div><Label>RFC</Label><input value={form.rfc} onChange={e => set('rfc', e.target.value)} placeholder="CAR210511M41" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} /></div>
          <div><Label>Email</Label><input value={form.email} onChange={e => set('email', e.target.value)} placeholder="proveedor@email.com" style={css.input(false)} /></div>
          <div>
            <Label>Moneda</Label>
            <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
              <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <CuentasProveedorEditor cuentas={form.cuentas} onChange={cs => set('cuentas', cs)} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Label>Motivos de Pago permitidos</Label>
            <input value={motivoQ} onChange={e => setMotivoQ(e.target.value)} placeholder="Filtrar motivos..."
              style={{ ...css.input(false), marginBottom: 7 }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 160, overflowY: 'auto' }}>
              {motivosFiltrados.map(m => {
                const sel = form.motivosPago.includes(m);
                return (
                  <button key={m} onClick={() => toggleMotivo(m)} style={{
                    padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', transition: 'all 0.12s',
                    background: sel ? 'var(--plasma)' : 'var(--hair-soft)', color: sel ? 'white' : 'var(--ink-soft)',
                    border: `1.5px solid ${sel ? 'var(--plasma)' : 'var(--hair)'}`,
                  }}>{m}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
              {form.motivosPago.length === 0
                ? 'Sin selección — el proveedor puede usar todos los motivos'
                : `${form.motivosPago.length} motivo(s) asignado(s)`}
            </div>
          </div>
          <div style={{ gridColumn: 'span 2', display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Sucursales ─────────────────────────────────────────────────────────────
function Sucursales() {
  const [items, setItems] = useState([]);
  const [centrosTodos, setCentrosTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '' });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Sucursales',
    columnas: [
      { key: 'nombre',    label: 'Nombre de Sucursal', required: true, ejemplo: 'POLANCO PISO 13' },
      { key: 'ubicacion', label: 'Ubicación (CORPORATIVO, CEDIS, TIENDA o FONTASTIC)', required: false, ejemplo: 'CORPORATIVO' },
    ],
    onImport: async (rows) => api.post('/sucursales/import', { items: rows }),
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/sucursales'),
      api.get('/centros/all?limit=500'),
    ]).then(([sucs, cc]) => {
      setItems(sucs);
      setCentrosTodos(cc.items || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const centrosDe = (nombreSucursal) =>
    centrosTodos.filter(c => c.sucursales?.includes(nombreSucursal));

  const open = (item = null) => {
    setEditing(item);
    setForm(item ? { nombre: item.nombre, ubicacion: item.ubicacion || '' } : { nombre: '', ubicacion: '' });
    setModal(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/sucursales/${editing._id}`, form);
      else await api.post('/sucursales', form);
      toast_('Sucursal guardada'); setModal(false); load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Desactivar sucursal?')) return;
    try { await api.del(`/sucursales/${id}`); toast_('Sucursal desactivada'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={load} />
      <CrudTable titulo="Sucursales" cols={['Nombre', 'Ubicación', 'Centros de Costos asignados']}
        items={items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)} loading={loading}
        onAdd={() => open()} onEdit={open} onDelete={del}
        extraActions={<button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>}
        footer={<Paginacion page={page} total={items.length} limit={PAGE_SIZE} onChange={setPage} />}
        renderCell={(col, item) => {
          if (col === 'Nombre') return <span style={{ fontWeight: 500 }}>{item.nombre}</span>;
          if (col === 'Ubicación') return <span style={{ color: 'var(--glass)', fontSize: 12 }}>{item.ubicacion || 'Sin clasificar'}</span>;
          if (col === 'Centros de Costos asignados') {
            const cc = centrosDe(item.nombre);
            return cc.length
              ? <span style={{ color: 'var(--st-por-pagar)', fontSize: 12 }}>{cc.length} centro(s)</span>
              : <span style={{ color: '#f59e0b', fontSize: 12 }}>Sin centros asignados</span>;
          }
        }}
      />
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Sucursal' : 'Nueva Sucursal'} width={540}>
        <div style={{ marginBottom: 16 }}>
          <Label required>Nombre de Sucursal</Label>
          <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} style={css.input(false)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Label>Ubicación</Label>
          <select value={form.ubicacion || ''} onChange={e => setForm(f => ({ ...f, ubicacion: e.target.value }))} style={css.input(false)}>
            <option value="">Sin clasificar</option>
            {['CORPORATIVO', 'CEDIS', 'TIENDA', 'FONTASTIC'].map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>Agrupa el gasto en los tableros (cubo de gastos).</div>
        </div>
        {editing && (() => {
          const cc = centrosDe(editing.nombre);
          return cc.length > 0 && (
            <div>
              <Label>Centros de Costos asignados</Label>
              <div style={{ marginTop: 6, padding: '10px 12px', background: 'rgba(14,165,233,0.10)', borderRadius: 8, border: '1px solid rgba(14,165,233,0.28)', maxHeight: 200, overflowY: 'auto' }}>
                {[...new Set(cc.map(c => c.seccion).filter(Boolean))].map(sec => (
                  <div key={sec} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--st-por-pagar)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{sec}</div>
                    {cc.filter(c => c.seccion === sec).map(c => (
                      <div key={c._id} style={{ fontSize: 12, color: 'var(--ink)', padding: '2px 0 2px 8px', borderLeft: '2px solid rgba(14,165,233,0.28)' }}>{c.nombre}</div>
                    ))}
                  </div>
                ))}
                {cc.filter(c => !c.seccion).map(c => (
                  <div key={c._id} style={{ fontSize: 12, color: 'var(--ink)', padding: '2px 0' }}>{c.nombre}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>Para modificarlos ve a la pestaña Centros de Costos.</div>
            </div>
          );
        })()}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
          <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
        </div>
      </Modal>
    </div>
  );
}

// ── Centros de Costos ──────────────────────────────────────────────────────
function CentrosCostos() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', seccion: '', tipoGasto: '', sucursales: [] });
  const [importModal, setImportModal] = useState(false);
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Centros de Costos',
    columnas: [
      { key: 'nombre',     label: 'Nombre del Centro',           required: true,  ejemplo: 'DIRECCION GENERAL' },
      { key: 'seccion',    label: 'Sección / Agrupación',        required: false, ejemplo: 'DIRECCION GENERAL' },
      { key: 'sucursales', label: 'Sucursales (separadas por |)', required: false, ejemplo: 'POLANCO|CENTRO' },
      { key: 'tipoGasto',  label: 'Tipo de gasto (VENTA o ADMINISTRACION)', required: false, ejemplo: 'ADMINISTRACION' },
    ],
    onImport: async (rows) => api.post('/centros/import', { items: rows }),
  };

  const load = (query = q, p = page) => {
    setLoading(true);
    api.get(`/centros/all?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}`)
      .then(setData).finally(() => setLoading(false));
  };
  useEffect(() => {
    load('');
    api.get('/sucursales').then(s => setSucursales(s)).catch(() => {});
  }, []); // eslint-disable-line

  const open = (item = null) => {
    setEditing(item);
    setForm(item
      ? { nombre: item.nombre, seccion: item.seccion || '', tipoGasto: item.tipoGasto || '', sucursales: item.sucursales || [] }
      : { nombre: '', seccion: '', tipoGasto: '', sucursales: [] });
    setModal(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast_('El nombre es requerido', 'error'); return; }
    try {
      if (editing) await api.put(`/centros/${editing._id}`, form);
      else await api.post('/centros', form);
      toast_('Centro de costos guardado'); setModal(false); load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Desactivar este centro de costos?')) return;
    try { await api.del(`/centros/${id}`); toast_('Centro desactivado'); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const toggleSuc = (suc) => {
    setForm(f => ({
      ...f,
      sucursales: f.sucursales.includes(suc)
        ? f.sucursales.filter(s => s !== suc)
        : [...f.sucursales, suc],
    }));
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }}
            placeholder="Buscar centro o sección..." style={{ ...css.input(false), width: 280 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />

      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : data.items.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin registros</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Nombre', 'Sección', 'Tipo de gasto', 'Sucursales donde aplica', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontWeight: 500 }}>{item.nombre}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', fontSize: 12 }}>{item.seccion || '—'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', fontSize: 12 }}>{item.tipoGasto === 'VENTA' ? 'Venta' : item.tipoGasto === 'ADMINISTRACION' ? 'Administración' : 'Sin clasificar'}</td>
                  <td style={{ padding: '9px 14px', fontSize: 11, color: 'var(--ink-soft)', maxWidth: 320 }}>
                    {item.sucursales?.length
                      ? item.sucursales.map(s => (
                        <span key={s} style={{ display: 'inline-block', background: 'rgba(14,165,233,0.10)', border: '1px solid rgba(14,165,233,0.28)', color: 'var(--st-por-pagar)', borderRadius: 4, padding: '1px 7px', marginRight: 4, marginBottom: 2, fontSize: 11 }}>{s}</span>
                      ))
                      : <span style={{ color: 'var(--glass)' }}>Todas</span>}
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(item)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => del(item._id)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--danger)' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Centro de Costos' : 'Nuevo Centro de Costos'} width={540}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><Label required>Nombre del Centro</Label><input value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej. DIRECCION GENERAL" style={css.input(false)} /></div>
          <div><Label>Sección / Agrupación</Label><input value={form.seccion} onChange={e => set('seccion', e.target.value)} placeholder="Ej. DIRECCION GENERAL" style={css.input(false)} /></div>
          <div>
            <Label>Tipo de gasto</Label>
            <select value={form.tipoGasto || ''} onChange={e => set('tipoGasto', e.target.value)} style={css.input(false)}>
              <option value="">Sin clasificar</option>
              <option value="VENTA">Venta</option>
              <option value="ADMINISTRACION">Administración</option>
            </select>
          </div>
          <div>
            <Label>Aplica en sucursales</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
              {sucursales.map(suc => {
                const selected = form.sucursales.includes(suc.nombre);
                return (
                  <button key={suc._id} onClick={() => toggleSuc(suc.nombre)} style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                    background: selected ? 'var(--plasma)' : 'var(--hair-soft)',
                    color: selected ? 'white' : 'var(--ink-soft)',
                    border: `1.5px solid ${selected ? 'var(--plasma)' : 'var(--hair)'}`,
                  }}>{suc.nombre}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 5 }}>
              {form.sucursales.length === 0 ? 'Sin selección = aparece en todas las sucursales' : `${form.sucursales.length} sucursal(es) seleccionada(s)`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Motivos de Pago / Débito ───────────────────────────────────────────────
// Reutilizable: por defecto administra /motivos (pago); con props apunta al
// catálogo de débito (/motivos-debito).
function MotivosPago({ endpoint = '/motivos', titulo = 'Motivos de Pago' }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [verTodos, setVerTodos] = useState(false);
  const [form, setForm] = useState({ nombre: '', cuentas: '', cuentaAgrupadora: '' });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo,
    columnas: [
      { key: 'nombre',           label: titulo,                                 required: true,  ejemplo: 'SERVICIOS BASICOS' },
      { key: 'cuentas',          label: 'Cuentas contables (separadas por |)',  required: false, ejemplo: '6000-004-001|6100-004-001' },
      { key: 'cuentaAgrupadora', label: 'Cuenta agrupadora',                    required: false, ejemplo: 'GASTOS DE OPERACIÓN' },
    ],
    onImport: async (rows) => api.post(`${endpoint}/import`, { items: rows }),
  };

  // Los motivos no se borran, se dan de baja: sin esta casilla, uno dado de baja
  // por error desaparecía de la lista y ya no había forma de reactivarlo.
  const load = (query = q, p = page, todos = verTodos) => {
    setLoading(true);
    api.get(`${endpoint}/all?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}${todos ? '&todos=1' : ''}`)
      .then(setData).finally(() => setLoading(false));
  };
  useEffect(() => load(''), []); // eslint-disable-line

  const toggleVerTodos = (valor) => { setVerTodos(valor); setPage(1); load(q, 1, valor); };

  const reactivar = async (item) => {
    try { await api.put(`${endpoint}/${item._id}`, { activo: true }); toast_(`«${item.nombre}» reactivado`); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const open = (item = null) => {
    setEditing(item);
    setForm(item
      ? { nombre: item.nombre, cuentas: (item.cuentasContables || []).join(', '), cuentaAgrupadora: item.cuentaAgrupadora || '' }
      : { nombre: '', cuentas: '', cuentaAgrupadora: '' });
    setModal(true);
  };

  // El nombre de un motivo es la llave que liga a sus solicitudes, así que el
  // backend bloquea renombrar uno que ya esté en uso (409). Sólo un admin puede
  // forzarlo, y para eso hay que preguntarle antes.
  // OJO: el botón usa onClick={save}, así que el primer argumento puede llegar
  // siendo el evento del click — sólo un `true` explícito cuenta como confirmación.
  const save = async (confirmar) => {
    if (!form.nombre.trim()) { toast_('El nombre es requerido', 'error'); return; }
    try {
      const payload = {
        nombre: form.nombre.trim(),
        // acepta comas, punto y coma o saltos de línea como separador
        cuentasContables: form.cuentas.split(/[,;\n]/).map(s => s.trim()).filter(Boolean),
        cuentaAgrupadora: form.cuentaAgrupadora.trim(),
        ...(confirmar === true ? { confirmarRenombre: true } : {}),
      };
      if (editing) await api.put(`${endpoint}/${editing._id}`, payload);
      else await api.post(endpoint, payload);
      toast_('Motivo guardado'); setModal(false); load(q);
    } catch (e) {
      if (e.status === 409 && e.datos?.requiereConfirmacion && confirmar !== true) {
        if (window.confirm(`⚠️ ${e.message}\n\n¿Renombrarlo de todas formas, bajo tu responsabilidad?`)) return save(true);
        return;
      }
      toast_(e.message, 'error');
    }
  };

  // Dar de baja, no borrar: el motivo se conserva en la base porque las solicitudes
  // que ya lo usan lo referencian por nombre.
  const del = async (item) => {
    if (!window.confirm(
      `¿Dar de baja «${item.nombre}»?\n\n` +
      'Dejará de aparecer al capturar nuevas solicitudes. Las que ya lo usan no se tocan: ' +
      'conservan su motivo, su cuenta contable y su cuenta agrupadora.'
    )) return;
    try { await api.del(`${endpoint}/${item._id}`); toast_(`«${item.nombre}» dado de baja`); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }}
            placeholder="Buscar motivo..." style={{ ...css.input(false), width: 300 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
          <label title="Los motivos no se borran: se dan de baja. Actívala para verlos y poder reactivarlos."
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--glass)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={verTodos} onChange={e => toggleVerTodos(e.target.checked)} />
            Ver los dados de baja
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : data.items.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin registros</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {[titulo, 'Cuentas contables', 'Cuenta agrupadora', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontWeight: 500 }}>
                    {item.nombre}
                    {item.activo === false && (
                      <span style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'rgba(180,83,9,0.10)', color: 'var(--st-pendiente)', border: '1px solid rgba(180,83,9,0.28)' }}>DADO DE BAJA</span>
                    )}
                  </td>
                  {/* Cuentas del plan contable (informativo; el solicitante nunca las ve) */}
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: 'var(--glass)' }}
                    title={(item.cuentasContables || []).join('\n')}>
                    {item.cuentasContables?.length
                      ? (item.cuentasContables.length <= 2
                          ? item.cuentasContables.join(', ')
                          : `${item.cuentasContables[0]} +${item.cuentasContables.length - 1} más`)
                      : '—'}
                  </td>
                  <td style={{ padding: '9px 14px', fontSize: 12.5, color: 'var(--ink-soft)' }}>{item.cuentaAgrupadora || '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(item)} title="Editar motivo" style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️ Editar</button>
                      {/* No es una papelera: el motivo NO se borra, se da de baja.
                          Tiene que conservarse porque las solicitudes que ya lo usan
                          lo referencian por nombre (ver utils/motivoEnUso.js). */}
                      {item.activo === false ? (
                        <button onClick={() => reactivar(item)} title="Volver a ofrecerlo al capturar solicitudes"
                          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(22,163,74,0.28)', background: 'rgba(22,163,74,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--ok)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          ↩️ Reactivar
                        </button>
                      ) : (
                        <button onClick={() => del(item)}
                          title="Dar de baja: deja de ofrecerse al capturar, pero las solicitudes que ya lo usan lo conservan"
                          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(180,83,9,0.28)', background: 'rgba(180,83,9,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--st-pendiente)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          🚫 Desactivar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Motivo' : 'Nuevo Motivo'}>
        <Grid cols={1} gap={12}>
          <div>
            <Label required>Nombre del Motivo</Label>
            <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej. SERVICIOS BASICOS" style={css.input(false)} />
            {editing && form.nombre.trim() !== editing.nombre && (
              <div style={{ fontSize: 11, color: 'var(--st-pendiente)', marginTop: 4 }}>
                ⚠️ Estás cambiando el nombre. Es la llave que liga este motivo con sus solicitudes:
                si alguna lo usa, se quedarían sin cuenta contable ni cuenta agrupadora. Para corregir
                un motivo ya usado, desactívalo y da de alta uno nuevo.
              </div>
            )}
          </div>
          <div>
            <Label>Cuentas contables asociadas</Label>
            <input value={form.cuentas} onChange={e => setForm(f => ({ ...f, cuentas: e.target.value }))}
              placeholder="Ej. 6000-004-001, 6100-004-001" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace', fontSize: 12.5 }} />
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
              Separadas por coma. El solicitante no las ve; se usan para reportes contables.
            </div>
          </div>
          <div>
            <Label>Cuenta agrupadora</Label>
            <input value={form.cuentaAgrupadora} onChange={e => setForm(f => ({ ...f, cuentaAgrupadora: e.target.value }))}
              placeholder="Ej. GASTOS DE OPERACIÓN" style={css.input(false)} />
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
              Concepto agrupador de este motivo. Se copia a la solicitud y sale en el Reporte Pipeline.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Conceptos de Comprobante (tipo de documento) ──────────────────────────
function ConceptosComprobante() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '' });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Conceptos de Comprobante',
    columnas: [{ key: 'nombre', label: 'Concepto de Comprobante', required: true, ejemplo: 'FACTURA' }],
    onImport: async (rows) => api.post('/conceptos-comprobante/import', { items: rows }),
  };

  const load = (query = q, p = page) => {
    setLoading(true);
    api.get(`/conceptos-comprobante/all?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}`).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(''); }, []); // eslint-disable-line

  const open = (item = null) => {
    setEditing(item);
    setForm(item ? { nombre: item.nombre } : { nombre: '' });
    setModal(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast_('El nombre es requerido', 'error'); return; }
    try {
      if (editing) await api.put(`/conceptos-comprobante/${editing._id}`, form);
      else await api.post('/conceptos-comprobante', form);
      toast_('Concepto guardado'); setModal(false); load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Desactivar este concepto?')) return;
    try { await api.del(`/conceptos-comprobante/${id}`); toast_('Concepto desactivado'); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }}
            placeholder="Buscar concepto de comprobante..." style={{ ...css.input(false), width: 320 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : data.items.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin registros</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Concepto de Comprobante', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                      background: 'rgba(22,163,74,0.10)', color: 'var(--ok)', border: '1px solid rgba(22,163,74,0.28)',
                      fontSize: 12, fontWeight: 600,
                    }}>{item.nombre}</span>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(item)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => del(item._id)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--danger)' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Concepto' : 'Nuevo Concepto de Comprobante'}>
        <Grid cols={1} gap={12}>
          <div>
            <Label required>Nombre del Concepto</Label>
            <input value={form.nombre} onChange={e => setForm({ nombre: e.target.value.toUpperCase() })}
              placeholder="Ej. FACTURA, TICKET, RECIBO..." style={css.input(false)} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Conceptos de Comprobación ──────────────────────────────────────────────
function ConceptosComprobacion() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '' });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Conceptos de Pago',
    columnas: [{ key: 'nombre', label: 'Concepto de Pago (Gasto)', required: true, ejemplo: 'COMPROBACION GASTOS CAJA CHICA DEDUCIBLE' }],
    onImport: async (rows) => api.post('/conceptos-comprobacion/import', { items: rows }),
  };

  const load = (query = q, p = page) => {
    setLoading(true);
    api.get(`/conceptos-comprobacion/all?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}`).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(''); }, []); // eslint-disable-line

  const open = (item = null) => {
    setEditing(item);
    setForm(item ? { nombre: item.nombre } : { nombre: '' });
    setModal(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast_('El nombre es requerido', 'error'); return; }
    try {
      if (editing) await api.put(`/conceptos-comprobacion/${editing._id}`, form);
      else await api.post('/conceptos-comprobacion', form);
      toast_('Concepto guardado'); setModal(false); load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Desactivar este concepto?')) return;
    try { await api.del(`/conceptos-comprobacion/${id}`); toast_('Concepto desactivado'); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }}
            placeholder="Buscar concepto..." style={{ ...css.input(false), width: 300 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : data.items.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin registros</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Concepto de Pago (Gasto)', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontWeight: 500 }}>{item.nombre}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(item)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => del(item._id)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--danger)' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Concepto' : 'Nuevo Concepto de Pago'}>
        <Grid cols={1} gap={12}>
          <div>
            <Label required>Nombre del Concepto</Label>
            <input value={form.nombre} onChange={e => setForm({ nombre: e.target.value.toUpperCase() })}
              placeholder="Ej. COMPROBACION GASTOS CAJA CHICA DEDUCIBLE" style={css.input(false)} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Comprobadores de Gastos ────────────────────────────────────────────────
function ComprobadoresGastos() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [conceptosCat, setConceptosCat] = useState([]);
  const [conceptoQ, setConceptoQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', banco: '', clabe: '', cuenta: '', conceptos: [] });
  const [importModal, setImportModal] = useState(false);
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo: 'Comprobadores de Gastos',
    columnas: [
      { key: 'nombre',    label: 'Nombre (Comprobador)',           required: true,  ejemplo: 'GRISELDA ALAMILLA GONZALEZ' },
      { key: 'banco',     label: 'Banco',                          required: false, ejemplo: 'BBVA' },
      { key: 'clabe',     label: 'CLABE',                          required: false, ejemplo: '012345678901234567' },
      { key: 'cuenta',    label: 'No. Cuenta',                     required: false, ejemplo: '1234567890' },
      { key: 'conceptos', label: 'Conceptos (separados por |)',    required: false, ejemplo: 'CAJA CHICA DEDUCIBLE|CAJA CHICA NO DEDUCIBLE' },
    ],
    onImport: async (rows) => api.post('/comprobadores/import', { items: rows }),
  };

  const load = (query = q, p = page) => {
    setLoading(true);
    api.get(`/comprobadores?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}`).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => {
    load('');
    api.get('/conceptos-comprobacion?limit=200').then(d => setConceptosCat(d.items || [])).catch(() => {});
  }, []); // eslint-disable-line

  const open = (item = null) => {
    setEditing(item);
    setConceptoQ('');
    setForm(item
      ? { nombre: item.nombre, banco: item.banco || '', clabe: item.clabe || '', cuenta: item.cuenta || '', conceptos: item.conceptos || [] }
      : { nombre: '', banco: '', clabe: '', cuenta: '', conceptos: [] });
    setModal(true);
  };

  const toggleConcepto = (nombre) => setForm(f => ({
    ...f,
    conceptos: f.conceptos.includes(nombre)
      ? f.conceptos.filter(c => c !== nombre)
      : [...f.conceptos, nombre],
  }));

  const save = async () => {
    if (!form.nombre.trim()) { toast_('El nombre es requerido', 'error'); return; }
    try {
      if (editing) await api.put(`/comprobadores/${editing._id}`, form);
      else await api.post('/comprobadores', form);
      toast_('Comprobador guardado'); setModal(false); load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Desactivar comprobador?')) return;
    try { await api.del(`/comprobadores/${id}`); toast_('Comprobador desactivado'); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const conceptosFiltrados = conceptoQ
    ? conceptosCat.filter(c => c.nombre.toLowerCase().includes(conceptoQ.toLowerCase()))
    : conceptosCat;

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }}
            placeholder="Buscar por nombre..." style={{ ...css.input(false), width: 300 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Nombre (Comprobador)', 'Banco', 'CLABE', 'Conceptos de Gasto asignados', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontWeight: 600, color: 'var(--ink)', maxWidth: 220 }}>{item.nombre}</td>
                  <td style={{ padding: '9px 14px', color: item.banco ? 'var(--glass)' : 'var(--glass)' }}>{item.banco || '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11, color: item.clabe ? 'var(--glass)' : 'var(--glass)' }}>{item.clabe || '—'}</td>
                  <td style={{ padding: '9px 14px', maxWidth: 380 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(item.conceptos || []).map(c => (
                        <span key={c} style={{
                          display: 'inline-block', background: 'rgba(14,165,233,0.10)', border: '1px solid rgba(14,165,233,0.28)',
                          color: 'var(--st-por-pagar)', borderRadius: 4, padding: '2px 8px', fontSize: 11,
                        }}>{c}</span>
                      ))}
                      {(!item.conceptos || item.conceptos.length === 0) && <span style={{ color: 'var(--glass)', fontSize: 12 }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(item)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => del(item._id)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--danger)' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Comprobador' : 'Nuevo Comprobador'} width={560}>
        <Grid cols={1} gap={14}>
          <div>
            <Label required>Nombre (Proveedor Consolidado)</Label>
            <input value={form.nombre} onChange={e => set('nombre', e.target.value)}
              placeholder="Ej. GRISELDA ALAMILLA GONZALEZ" style={css.input(false)} />
          </div>
          <Grid cols={3} gap={12}>
            <div><Label>Banco</Label><input value={form.banco} onChange={e => set('banco', e.target.value)} placeholder="BBVA, BANORTE..." style={css.input(false)} /></div>
            <div><Label>CLABE Interbancaria</Label><input value={form.clabe} onChange={e => set('clabe', e.target.value)} placeholder="18 dígitos" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} /></div>
            <div><Label>No. de Cuenta</Label><input value={form.cuenta} onChange={e => set('cuenta', e.target.value)} style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} /></div>
          </Grid>
          <div>
            <Label>Conceptos de Pago permitidos</Label>
            <input value={conceptoQ} onChange={e => setConceptoQ(e.target.value)}
              placeholder="Filtrar conceptos..."
              style={{ ...css.input(false), marginBottom: 7 }} />
            {conceptosCat.length === 0 ? (
              <div style={{ padding: '12px', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.32)', borderRadius: 8, fontSize: 12, color: 'var(--st-pendiente)' }}>
                No hay conceptos en el catálogo. Agrégalos primero en la pestaña "Conceptos de Pago".
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)', maxHeight: 200, overflowY: 'auto' }}>
                {conceptosFiltrados.map(c => {
                  const sel = form.conceptos.includes(c.nombre);
                  return (
                    <button key={c._id} onClick={() => toggleConcepto(c.nombre)} style={{
                      padding: '5px 11px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif', transition: 'all 0.12s',
                      background: sel ? '#0369a1' : 'var(--hair-soft)',
                      color: sel ? 'white' : 'var(--ink-soft)',
                      border: `1.5px solid ${sel ? '#0369a1' : 'var(--hair)'}`,
                    }}>{c.nombre}</button>
                  );
                })}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
              {form.conceptos.length === 0
                ? 'Sin selección — asigna al menos un concepto'
                : `${form.conceptos.length} concepto(s) seleccionado(s)`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Catálogo genérico tipo proveedor (Clientes, Finiquitos) ─────────────────
function CatalogoTipoProveedor({ basePath, titulo, singular }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { nombre: '', beneficiario: '', banco: '', clabe: '', cuenta: '', moneda: 'MXN', clave: '', categoria: '', rfc: '', email: '' };
  const [form, setForm] = useState({ ...EMPTY });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };
  const importConfig = {
    titulo,
    columnas: [
      { key: 'nombre',      label: 'Nombre / Razón Social', required: true,  ejemplo: `${singular.toUpperCase()} ABC S.A.` },
      { key: 'beneficiario',label: 'Beneficiario',          required: false, ejemplo: 'JUAN GARCIA' },
      { key: 'banco',       label: 'Banco',                 required: false, ejemplo: 'BBVA' },
      { key: 'clabe',       label: 'CLABE',                 required: false, ejemplo: '012345678901234567' },
      { key: 'cuenta',      label: 'No. Cuenta',            required: false, ejemplo: '1234567890' },
      { key: 'moneda',      label: 'Moneda',                required: false, ejemplo: 'MXN' },
      { key: 'clave',       label: 'Clave',                 required: false, ejemplo: '10001' },
      { key: 'categoria',   label: 'Categoría',             required: false, ejemplo: 'GENERAL' },
      { key: 'rfc',         label: 'RFC',                   required: false, ejemplo: 'XAXX010101000' },
      { key: 'email',       label: 'Email',                 required: false, ejemplo: 'correo@correo.com' },
    ],
    onImport: async (rows) => api.post(`${basePath}/import`, { items: rows }),
  };

  const load = (query = q, p = page) => {
    setLoading(true);
    api.get(`${basePath}?q=${encodeURIComponent(query)}&page=${p}&limit=${PAGE_SIZE}`).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(''); }, []); // eslint-disable-line

  const open = (item = null) => {
    setEditing(item);
    setForm(item ? {
      nombre: item.nombre, beneficiario: item.beneficiario || '', banco: item.banco || '',
      clabe: item.clabe || '', cuenta: item.cuenta || '', moneda: item.moneda || 'MXN',
      clave: item.clave || '', categoria: item.categoria || '', rfc: item.rfc || '', email: item.email || '',
    } : { ...EMPTY });
    setModal(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`${basePath}/${editing._id}`, form);
      else await api.post(basePath, form);
      toast_(`${singular} guardado`); setModal(false); load(q);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm(`¿Desactivar ${singular.toLowerCase()}?`)) return;
    try { await api.del(`${basePath}/${id}`); toast_(`${singular} desactivado`); load(q); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={() => load(q)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(q, 1); } }} placeholder="Buscar por nombre, RFC, categoría..." style={{ ...css.input(false), width: 300 }} />
          <button onClick={() => { setPage(1); load(q, 1); }} style={css.btn('#0369a1')}>Buscar</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--glass)' }}>{data.total} total</span>
          <button onClick={() => setImportModal(true)} style={{ ...css.btn('#0369a1'), fontSize: 12 }}>⬆️ Importar</button>
          <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
        </div>
      </div>
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Clave', 'Nombre', 'Categoría', 'RFC', 'Banco', 'CLABE', 'Moneda', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map(p => (
                <tr key={p._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--glass)' }}>{p.clave || '—'}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.categoria || '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>{p.rfc || '—'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)' }}>{p.banco || '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>{p.clabe || '—'}</td>
                  <td style={{ padding: '9px 14px' }}><Badge color="#0369a1">{p.moneda}</Badge></td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => open(p)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => del(p._id)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', cursor: 'pointer', fontSize: 12, color: 'var(--danger)' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginacion page={page} total={data.total} limit={PAGE_SIZE} onChange={p => { setPage(p); load(q, p); }} />
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? `Editar ${singular}` : `Nuevo ${singular}`} width={560}>
        <Grid cols={2} gap={12}>
          <div style={{ gridColumn: 'span 2' }}><Label required>Nombre / Razón Social</Label><input value={form.nombre} onChange={e => set('nombre', e.target.value)} style={css.input(false)} /></div>
          <div style={{ gridColumn: 'span 2' }}><Label>Beneficiario (si difiere del nombre)</Label><input value={form.beneficiario} onChange={e => set('beneficiario', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Clave</Label><input value={form.clave} onChange={e => set('clave', e.target.value)} placeholder="10001" style={css.input(false)} /></div>
          <div><Label>Categoría</Label><input value={form.categoria} onChange={e => set('categoria', e.target.value)} style={css.input(false)} /></div>
          <div><Label>RFC</Label><input value={form.rfc} onChange={e => set('rfc', e.target.value)} placeholder="CAR210511M41" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} /></div>
          <div><Label>Email</Label><input value={form.email} onChange={e => set('email', e.target.value)} placeholder="correo@email.com" style={css.input(false)} /></div>
          <div><Label>Banco</Label><input value={form.banco} onChange={e => set('banco', e.target.value)} placeholder="BBVA, BANORTE..." style={css.input(false)} /></div>
          <div>
            <Label>Moneda</Label>
            <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
              <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}><Label>CLABE Interbancaria</Label><input value={form.clabe} onChange={e => set('clabe', e.target.value)} placeholder="18 dígitos" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} /></div>
          <div style={{ gridColumn: 'span 2' }}><Label>No. de Cuenta</Label><input value={form.cuenta} onChange={e => set('cuenta', e.target.value)} style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} /></div>
          <div style={{ gridColumn: 'span 2', display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}

// ── Avisos (mensaje que le salta a todos los usuarios al iniciar sesión) ────
function Avisos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titulo: '', mensaje: '' });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };

  const load = () => { setLoading(true); api.get('/avisos').then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const open = (item = null) => {
    setEditing(item);
    setForm(item ? { titulo: item.titulo, mensaje: item.mensaje } : { titulo: '', mensaje: '' });
    setModal(true);
  };

  const save = async () => {
    if (!form.titulo.trim() || !form.mensaje.trim()) { toast_('Título y mensaje son requeridos', 'error'); return; }
    try {
      if (editing) await api.put(`/avisos/${editing._id}`, form);
      else await api.post('/avisos', form);
      toast_('Aviso guardado'); setModal(false); load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const toggleActivo = async (item) => {
    try { await api.put(`/avisos/${item._id}`, { activo: !item.activo }); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: 'var(--glass)' }}>
          El mensaje "activo" le aparece como modal a todos los usuarios al iniciar sesión, hasta que lo marquen como leído.
        </span>
        <button onClick={() => open()} style={css.btn('#5e72e4')}>+ Agregar</button>
      </div>
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : items.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin avisos</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--hair-soft)', borderBottom: '2px solid var(--hair)' }}>
                {['Título', 'Mensaje', 'Estado', 'Creado', 'Acciones'].map(c => (
                  <th key={c} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hair-soft)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontWeight: 500 }}>{item.titulo}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.mensaje}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <button onClick={() => toggleActivo(item)} title="Clic para cambiar" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
                      <Badge color={item.activo ? '#15803d' : '#dc2626'}>{item.activo ? 'Activo' : 'Inactivo'}</Badge>
                    </button>
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', fontSize: 12 }}>{item.createdAt ? new Date(item.createdAt).toLocaleDateString('es-MX') : ''}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <button onClick={() => open(item)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Aviso' : 'Nuevo Aviso'} width={520}>
        <Grid cols={1} gap={12}>
          <div><Label required>Título</Label><input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ej. Mantenimiento programado" style={css.input(false)} /></div>
          <div>
            <Label required>Mensaje</Label>
            <textarea value={form.mensaje} onChange={e => setForm(f => ({ ...f, mensaje: e.target.value }))}
              rows={5} placeholder="Escribe el mensaje que verán todos los usuarios..." style={{ ...css.input(false), resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal(false)} style={css.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={save} style={css.btn('#5e72e4')}>Guardar</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}


// ── Tipos de cambio de referencia ──────────────────────────────────────────
// Sirven para estimar en pesos las solicitudes en moneda extranjera que TODAVÍA
// no se programan; en cuanto se programan, cada una guarda el suyo y deja de
// depender de este catálogo. Por eso es una bitácora con fecha y no un número
// editable: el vigente es el más reciente de cada moneda, y queda el rastro.
function TiposCambio() {
  const MONEDAS = ['USD', 'EUR'];
  const [data, setData] = useState({ items: [], total: 0 });
  const [vigentes, setVigentes] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const vacio = () => ({ moneda: 'USD', valor: '', fecha: new Date().toLocaleDateString('en-CA'), nota: '' });
  const [form, setForm] = useState(vacio);
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/tipos-cambio?limit=${PAGE_SIZE}`).catch(() => ({ items: [], total: 0 })),
      api.get('/tipos-cambio/vigentes').catch(() => ({})),
    ]).then(([d, v]) => { setData(d); setVigentes(v); }).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const abrir = (item = null) => {
    setEditando(item);
    setForm(item
      ? { moneda: item.moneda, valor: String(item.valor ?? ''), fecha: new Date(item.fecha).toLocaleDateString('en-CA'), nota: item.nota || '' }
      : vacio());
    setModal(true);
  };

  const guardar = async () => {
    if (!(parseFloat(form.valor) > 0)) { toast_('El tipo de cambio debe ser mayor a cero', 'error'); return; }
    const body = { ...form, valor: parseFloat(form.valor) };
    try {
      if (editando) await api.put(`/tipos-cambio/${editando._id}`, body);
      else await api.post('/tipos-cambio', body);
      setModal(false);
      setEditando(null);
      setForm(vacio());
      toast_(editando ? 'Tipo de cambio corregido' : 'Tipo de cambio registrado');
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  // CrudTable entrega el _id, no el renglón: se busca para poder confirmar con datos
  const eliminar = async (id) => {
    const item = data.items.find(i => i._id === id) || {};
    const cuando = item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX') : '';
    if (!window.confirm(`¿Eliminar la cotización de ${item.moneda || ''} ${cuando}?`)) return;
    try { await api.del(`/tipos-cambio/${id}`); toast_('Eliminado'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {MONEDAS.map(m => (
          <div key={m} style={{ ...css.card, padding: '12px 18px', minWidth: 170 }}>
            <div style={{ fontSize: 10, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m} vigente</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: vigentes[m] ? 'var(--ink)' : '#dc2626', fontFamily: 'Geist Mono, monospace', marginTop: 2 }}>
              {vigentes[m] ? `$${vigentes[m].toLocaleString('es-MX', { minimumFractionDigits: 4 })}` : 'sin capturar'}
            </div>
          </div>
        ))}
      </div>

      {MONEDAS.some(m => !vigentes[m]) && (
        <div style={{ padding: '10px 14px', marginBottom: 14, background: 'rgba(245,207,74,0.08)', border: '1px solid #92400e', borderRadius: 8, fontSize: 12.5, color: 'var(--st-pendiente)' }}>
          Sin tipo de cambio de referencia, las solicitudes en esa moneda que aún no se programan
          se suman <b>1 a 1</b> contra los pesos en el tablero, que es justo lo que se quiere evitar.
        </div>
      )}

      <CrudTable
        titulo="Tipos de cambio de referencia"
        cols={['Moneda', 'Tipo de cambio', 'Fecha', 'Capturó', 'Nota']}
        items={data.items}
        loading={loading}
        onAdd={() => abrir()}
        onEdit={abrir}
        onDelete={eliminar}
        renderCell={(col, item) => (
          col === 'Moneda' ? <b>{item.moneda}</b> :
          col === 'Tipo de cambio' ? <span style={{ fontFamily: 'Geist Mono, monospace' }}>${(item.valor || 0).toLocaleString('es-MX', { minimumFractionDigits: 4 })}</span> :
          col === 'Fecha' ? new Date(item.fecha).toLocaleDateString('es-MX') :
          col === 'Capturó' ? (item.nombreUsuario || '—') :
          (item.nota || '—')
        )}
      />

      <Modal open={modal} onClose={() => { setModal(false); setEditando(null); }}
        title={editando ? 'Corregir tipo de cambio' : 'Registrar tipo de cambio'} width={420}>
        <Grid cols={2} gap={14}>
          <div>
            <Label required>Moneda</Label>
            <select value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))} style={css.select(false)}>
              {MONEDAS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <Label required>Pesos por 1 {form.moneda}</Label>
            <input type="number" step="0.0001" min="0" value={form.valor} placeholder="17.4523"
              onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
              style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} />
          </div>
        </Grid>
        <div style={{ marginTop: 14 }}>
          <Label required>Fecha de la cotización</Label>
          <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={css.input(false)} />
        </div>
        <div style={{ marginTop: 14 }}>
          <Label>Nota</Label>
          <input value={form.nota} onChange={e => setForm(f => ({ ...f, nota: e.target.value }))}
            placeholder="Ej. DOF, ventanilla BBVA..." style={css.input(false)} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={() => { setModal(false); setEditando(null); }} style={{ ...css.btn('#6b7280', true), fontSize: 13 }}>Cancelar</button>
          <button onClick={guardar} style={{ ...css.btn('#5e72e4'), fontSize: 13 }}>Guardar</button>
        </div>
      </Modal>
    </div>
  );
}

// ── Panel Principal ────────────────────────────────────────────────────────
const TABS_ADMIN = [
  { id: 'usuarios',                label: '👤 Usuarios',                  roles: ['admin'] },
  { id: 'avisos',                  label: '📢 Avisos',                    roles: ['admin'] },
  { id: 'aprobacion-requisiciones', label: '✅ Aprobación de requisiciones', roles: ['admin'] },
  { id: 'empresas',                label: '🏢 Compañías',                  roles: ['admin'] },
  { id: 'sucursales',              label: '🏬 Sucursales',                roles: ['admin'] },
  { id: 'proveedores',             label: '🤝 Proveedores',               roles: ['admin', 'finanzas'] },
  { id: 'clientes',                label: '💸 Clientes (Reembolsos)',     roles: ['admin', 'finanzas'] },
  { id: 'finiquitos',              label: '📁 Finiquitos (Altas)',        roles: ['admin', 'finanzas'] },
  { id: 'proveedores-importacion', label: '🚢 Proveedores Importación',   roles: ['admin', 'finanzas'] },
  { id: 'centros',                 label: '🗂️ Centros de Costos',        roles: ['admin', 'finanzas'] },
  { id: 'motivos',                 label: '💳 Motivos de Pago',           roles: ['admin', 'finanzas'] },
  { id: 'motivos-debito',          label: '🏦 Motivos de Débito',         roles: ['admin', 'finanzas'] },
  { id: 'motivos-contribuciones',  label: '🏛️ Motivos de Contribuciones', roles: ['admin', 'finanzas'] },
  { id: 'motivos-activos-fijos',   label: '🏗️ Motivos de Activos Fijos',  roles: ['admin', 'finanzas'] },
  { id: 'motivos-mercancias-nacionales', label: '📦 Motivos Mercancías Nac.', roles: ['admin', 'finanzas'] },
  { id: 'motivos-nomina',          label: '👥 Motivos de Nómina',          roles: ['admin', 'finanzas'] },
  { id: 'motivos-reembolso',       label: '💸 Motivos de Reembolso',      roles: ['admin', 'finanzas'] },
  { id: 'motivos-complemento-importacion', label: '🚢 Motivos Costo Importación', roles: ['admin', 'finanzas'] },
  { id: 'conceptos-comprobacion',  label: '📋 Conceptos de Pago',         roles: ['admin', 'finanzas'] },
  { id: 'conceptos-comprobante',   label: '🏷️ Conceptos de Comprobante', roles: ['admin', 'finanzas'] },
  { id: 'comprobadores',           label: '🧾 Comprobadores de Gastos',   roles: ['admin', 'finanzas'] },
  { id: 'tipos-cambio',            label: '💱 Tipos de Cambio',           roles: ['admin', 'finanzas'] },
];

export default function Admin({ userRol = 'admin', catalogosPermitidos = [] }) {
  // Además de los tabs que ya le da su rol, un usuario puede tener catálogos
  // puntuales asignados (User.catalogosPermitidos) independiente de su rol.
  const tabsVisibles = TABS_ADMIN.filter(t => t.roles.includes(userRol) || catalogosPermitidos.includes(t.id));
  const [tab, setTab] = useState(tabsVisibles[0]?.id || 'centros');

  const titulo = userRol !== 'admin'
    ? 'Gestión de Catálogos'
    : 'Panel de Administración';
  const subtitulo = userRol !== 'admin'
    ? 'Administración de centros de costos, motivos, conceptos y comprobadores'
    : 'Gestión de catálogos, usuarios y configuración del sistema';

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <PageHeader icon={userRol !== 'admin' ? '📂' : '⚙️'} title={titulo} subtitle={subtitulo} />

      {/* Grupos de tabs */}
      {userRol === 'admin' && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, paddingLeft: 4 }}>
            Solo Administrador
          </div>
          <div style={{ display: 'flex', gap: 4, background: 'var(--hair-soft)', borderRadius: 10, padding: 4, flexWrap: 'wrap', marginBottom: 8 }}>
            {TABS_ADMIN.filter(t => t.roles.length === 1).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '7px 14px', borderRadius: 7, border: 'none', fontSize: 12.5, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                background: tab === t.id ? 'white' : 'transparent',
                color: tab === t.id ? 'var(--plasma)' : 'var(--glass)',
                boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{t.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, paddingLeft: 4 }}>
            Catálogos (Admin y Finanzas)
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--hair-soft)', borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
        {tabsVisibles.filter(t => t.roles.includes('finanzas')).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '7px 14px', borderRadius: 7, border: 'none', fontSize: 12.5, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
            background: tab === t.id ? 'white' : 'transparent',
            color: tab === t.id ? 'var(--plasma)' : 'var(--glass)',
            boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'usuarios'               && userRol === 'admin' && <Usuarios />}
      {tab === 'avisos'                 && userRol === 'admin' && <Avisos />}
      {tab === 'aprobacion-requisiciones' && userRol === 'admin' && <ReglasAprobacion />}
      {tab === 'empresas'               && userRol === 'admin' && <Empresas />}
      {tab === 'sucursales'             && userRol === 'admin' && <Sucursales />}
      {tab === 'proveedores'            && ['admin', 'finanzas'].includes(userRol) && <Proveedores />}
      {tab === 'clientes'               && <CatalogoTipoProveedor basePath="/clientes" titulo="Clientes" singular="Cliente" />}
      {tab === 'finiquitos'             && <CatalogoTipoProveedor basePath="/catalogo-finiquitos" titulo="Finiquitos" singular="Finiquito" />}
      {tab === 'proveedores-importacion' && <CatalogoTipoProveedor basePath="/proveedores-importacion" titulo="Proveedores de Importación" singular="Proveedor de importación" />}
      {tab === 'centros'                && <CentrosCostos />}
      {tab === 'motivos'                && <MotivosPago />}
      {tab === 'motivos-debito'         && <MotivosPago endpoint="/motivos-debito" titulo="Motivo de Débito" />}
      {tab === 'motivos-contribuciones' && <MotivosPago endpoint="/motivos-contribuciones" titulo="Motivo de Contribución" />}
      {tab === 'motivos-activos-fijos'  && <MotivosPago endpoint="/motivos-activos-fijos" titulo="Motivo de Activo Fijo" />}
      {tab === 'motivos-mercancias-nacionales' && <MotivosPago endpoint="/motivos-mercancias-nacionales" titulo="Motivo de Mercancía Nacional" />}
      {tab === 'motivos-nomina'         && <MotivosPago endpoint="/motivos-nomina" titulo="Motivo de Nómina" />}
      {tab === 'motivos-reembolso'      && <MotivosPago endpoint="/motivos-reembolso" titulo="Motivo de Reembolso" />}
      {tab === 'motivos-complemento-importacion' && <MotivosPago endpoint="/motivos-complemento-importacion" titulo="Motivo de Costo de Importación" />}
      {tab === 'conceptos-comprobacion' && <ConceptosComprobacion />}
      {tab === 'conceptos-comprobante'  && <ConceptosComprobante />}
      {tab === 'comprobadores'          && <ComprobadoresGastos />}
      {tab === 'tipos-cambio'           && <TiposCambio />}
    </div>
  );
}
